import type { WeaponDefinition, WeaponContext, WeaponResult, GameWorld } from './types';
import { WA_PHYSICS } from '../core/constants';
import { getGameRNG } from '../core/rng';

interface RaycastResult {
  hit: boolean;
  x: number;
  y: number;
  distance: number;
  hitWorm?: any;
}

function raycastTerrain(
  world: GameWorld,
  startX: number,
  startY: number,
  dirX: number,
  dirY: number,
  maxDistance: number,
  shooterId: string,
): RaycastResult {
  const step = 4;
  let closestHit: RaycastResult = {
    hit: false,
    x: startX + dirX * maxDistance,
    y: startY + dirY * maxDistance,
    distance: maxDistance,
  };

  for (const targetWorm of world.getWorms()) {
    if (targetWorm.id === shooterId) continue;
    if (targetWorm.state === 'dead') continue;

    const center = targetWorm.getCenterPosition();
    const hitRadius = Math.max(WA_PHYSICS.WORM.WIDTH, WA_PHYSICS.WORM.HEIGHT) / 2;
    const hitDist = lineCircleIntersectionDistance(
      startX,
      startY,
      dirX,
      dirY,
      center.x,
      center.y,
      hitRadius,
    );

    if (hitDist !== null && hitDist < closestHit.distance) {
      closestHit = {
        hit: true,
        x: startX + dirX * hitDist,
        y: startY + dirY * hitDist,
        distance: hitDist,
        hitWorm: targetWorm,
      };
    }
  }

  for (let dist = step; dist <= maxDistance && dist < closestHit.distance; dist += step) {
    const x = startX + dirX * dist;
    const y = startY + dirY * dist;

    if (world.getTerrain().isSolid(x, y)) {
      if (dist < closestHit.distance) {
        closestHit = { hit: true, x, y, distance: dist, hitWorm: undefined };
      }
      break;
    }
  }

  return closestHit;
}

function lineCircleIntersectionDistance(
  x1: number,
  y1: number,
  dx: number,
  dy: number,
  cx: number,
  cy: number,
  r: number,
): number | null {
  const fx = x1 - cx;
  const fy = y1 - cy;

  const a = dx * dx + dy * dy;
  const b = 2 * (fx * dx + fy * dy);
  const c = fx * fx + fy * fy - r * r;

  const discriminant = b * b - 4 * a * c;
  if (discriminant < 0) return null;

  const sqrtD = Math.sqrt(discriminant);
  const t1 = (-b - sqrtD) / (2 * a);
  const t2 = (-b + sqrtD) / (2 * a);

  if (t1 >= 0) return t1;
  if (t2 >= 0) return t2;
  return null;
}

export const WeaponShotgun: WeaponDefinition = {
  id: 'shotgun',
  name: 'Shotgun',
  icon: 'shotgun.png',
  category: 'projectile',
  maxAmmo: -1,
  maxUsesPerTurn: 2,
  powerCharging: false,
  timerAdjustable: false,
  showAimGuide: true,
  allowRetreat: false,

  fire(ctx: WeaponContext): WeaponResult {
    const { worm, angle, world } = ctx;

    const rad = (angle * Math.PI) / 180;
    const aimRad = worm.facing === 'right' ? rad : Math.PI - rad;

    const startX = worm.position.x + WA_PHYSICS.WORM.WIDTH / 2;
    const startY = worm.position.y + WA_PHYSICS.WORM.HEIGHT / 2;

    const range: number = WA_PHYSICS.SHOTGUN.RANGE;
    const spreadRad = (WA_PHYSICS.SHOTGUN.SPREAD_ANGLE * Math.PI) / 180;

    let totalHits = 0;
    const rng = getGameRNG();

    for (let i = 0; i < WA_PHYSICS.SHOTGUN.PELLET_COUNT; i++) {
      const pelletAngle = aimRad + (rng.next() - 0.5) * spreadRad;
      const dirX = Math.cos(pelletAngle);
      const dirY = -Math.sin(pelletAngle);

      const result = raycastTerrain(world, startX, startY, dirX, dirY, range, worm.id);

      if (result.hitWorm) {
        const targetWorm = result.hitWorm;
        const pushDir = { x: dirX, y: dirY };
        targetWorm.takeDamage(
          WA_PHYSICS.SHOTGUN.DAMAGE_PER_PELLET,
          pushDir.x * WA_PHYSICS.SHOTGUN.KNOCKBACK,
          (pushDir.y - 0.3) * WA_PHYSICS.SHOTGUN.KNOCKBACK,
        );
        totalHits++;
      }

      world.createExplosion(result.x, result.y, 8, 0);
    }

    return {
      success: true,
      entities: [],
      message: totalHits > 0 ? `Hit ${totalHits} target(s)` : 'Missed',
    };
  },
};
